import React from 'react';
import { motion } from 'framer-motion';

/**
 * KairosLogo Component
 * 
 * Logo SVG officiel de KAIROS avec:
 * - Animation d'apparition au chargement
 * - Accent indigo sur le "O"
 * - Version responsive
 * 
 * Props:
 * - size: 'sm' | 'md' | 'lg' (défaut: 'md')
 * - animated: boolean (défaut: true)
 * - className: string
 */
const KairosLogo = ({ size = 'md', animated = true, className = '' }) => {
  const sizes = {
    sm: { width: 120, height: 32 },
    md: { width: 180, height: 48 },
    lg: { width: 300, height: 80 },
  };
  
  const { width, height } = sizes[size];
  
  // Animation variants for drawing effect
  const pathVariants = {
    hidden: {
      pathLength: 0,
      opacity: 0,
    },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { duration: 1.5, ease: 'easeInOut' },
        opacity: { duration: 0.3 },
      },
    },
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };
  
  const LogoSVG = animated ? motion.svg : 'svg';
  const LogoPath = animated ? motion.path : 'path';
  const LogoRect = animated ? motion.rect : 'rect';
  const LogoG = animated ? motion.g : 'g';
  
  return (
    <LogoSVG
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 300 80"
      width={width}
      height={height}
      fill="none"
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={`kairos-logo-svg ${className}`}
      variants={animated ? containerVariants : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    >
      <LogoG>
        {/* K */}
        <LogoPath
          d="M20,15 L20,65 M20,40 L45,15 M20,40 L45,65"
          stroke="#FFFFFF"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* A */}
        <LogoPath
          d="M55,65 L75,15 L95,65 M65,45 L85,45"
          stroke="#FFFFFF"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* I */}
        <LogoPath
          d="M115,15 L115,65"
          stroke="#FFFFFF"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* R */}
        <LogoPath
          d="M135,65 L135,15 L160,15 C170,15 175,25 175,32 C175,45 165,45 160,45 L135,45 M160,45 L175,65"
          stroke="#FFFFFF"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* O - Accent color (Indigo) */}
        <LogoRect
          x="195"
          y="15"
          width="50"
          height="50"
          rx="5"
          stroke="#2A3B8F"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* S */}
        <LogoPath
          d="M275,15 L255,15 L255,40 L275,40 L275,65 L255,65"
          stroke="#FFFFFF"
          strokeWidth="6"
          variants={animated ? pathVariants : undefined}
        />
        
        {/* Underline */}
        <LogoPath
          d="M10,75 L285,75"
          stroke="#2A3B8F"
          strokeWidth="2"
          variants={animated ? {
            hidden: { pathLength: 0, opacity: 0 },
            visible: {
              pathLength: 1,
              opacity: 0.5,
              transition: { delay: 1, duration: 0.8 },
            },
          } : undefined}
        />
      </LogoG>
    </LogoSVG>
  );
};

export default KairosLogo;
